import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, Circle, Phone, MessageCircle, Star, Clock, MapPin, Loader2, XCircle, QrCode, Package, ChefHat, Bike, Home, Ticket, ShieldCheck, Download, Share2, Receipt } from 'lucide-react';
import { toast } from 'sonner';
import Header from '../components/Header';
import { useApp } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { ordersApi, paymentsApi, orderQrApi } from '../services/api';
import DeliveryFeeBreakdown from '../components/DeliveryFeeBreakdown';
import api from '../services/api';
import LiveMap from '../components/LiveMap';
import ShareButton from '../components/ShareButton';
import { useTranslation } from 'react-i18next';

const ORDER_STATUSES = [
  { key: 'placed',           label: 'Order placed',      icon: Ticket,     hint: 'We received your order.' },
  { key: 'confirmed',        label: 'Store confirmed',   icon: Package,    hint: 'The store accepted your order.' },
  { key: 'preparing',        label: 'Preparing',         icon: ChefHat,    hint: 'The store is getting everything ready.' },
  { key: 'ready_for_pickup', label: 'Ready for pickup',  icon: MapPin,     hint: 'Waiting to be picked up.' },
  { key: 'on_the_way',       label: 'On the way',        icon: Bike,       hint: 'A driver is on the move.' },
  { key: 'delivered',        label: 'Delivered',         icon: Home,       hint: 'Enjoy!' },
];

const pickupFlow = ['placed', 'confirmed', 'preparing', 'ready_for_pickup', 'delivered'];
const deliveryFlow = ['placed', 'confirmed', 'preparing', 'on_the_way', 'delivered'];

const TrackOrder = () => {
  const { orderId } = useParams();
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();
  const { format: formatBase } = useApp();
  const format = (v) => formatBase(v, order?.currency);
  const { t } = useTranslation();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payment, setPayment] = useState(null);
  const [qr, setQr] = useState(null);
  const [qrOpen, setQrOpen] = useState(false);
  const pollRef = useRef(null);

  // Load order with polling
  useEffect(() => {
    let canceled = false;
    const load = async () => {
      try {
        const o = await ordersApi.get(orderId);
        if (!canceled) setOrder(o);
      } catch (e) {
        if (!canceled) setOrder(null);
      } finally {
        if (!canceled) setLoading(false);
      }
    };
    load();
    pollRef.current = setInterval(load, 7000); // live status updates every 7s
    return () => { canceled = true; if (pollRef.current) clearInterval(pollRef.current); };
  }, [orderId]);

  // Admin-editable per-stage status messages (fallback to built-in hints).
  const [statusMsgs, setStatusMsgs] = useState({});
  useEffect(() => {
    api.get('/status-messages').then((r) => setStatusMsgs(r.data || {})).catch(() => {});
  }, []);

  // Live driver location — poll every 5s while status is between accepted
  // and delivered so the customer sees an Uber-style live dot on the map.
  const [driverLive, setDriverLive] = useState(null);
  useEffect(() => {
    if (!order) return;
    const LIVE_STATES = new Set([
      'accepted', 'en_route_to_vendor', 'arrived_at_vendor',
      'picked_up', 'on_the_way', 'arrived_at_customer',
    ]);
    if (!LIVE_STATES.has(order.status)) { setDriverLive(null); return; }
    let alive = true;
    const tick = () => api.get(`/orders/${orderId}/driver-location`)
      .then(({ data }) => { if (alive) setDriverLive(data); })
      .catch(() => {});
    tick();
    const id = setInterval(tick, 5000);
    return () => { alive = false; clearInterval(id); };
  }, [orderId, order?.status]);

  // Toast the customer when a driver first accepts (foreground fallback for
  // the web-push notification). Fires exactly once per assignment.
  const acceptedToastRef = useRef(null);
  useEffect(() => {
    if (!order || !order.driver_id) return;
    if (acceptedToastRef.current === order.driver_id) return;
    if (order.status !== 'accepted') return;
    acceptedToastRef.current = order.driver_id;
    const d = order.driver || {};
    const star = d.rating ? `⭐ ${Number(d.rating).toFixed(1)}` : '';
    toast.success(`${d.name || 'Your driver'} accepted your order`, {
      description: [star, d.vehicle].filter(Boolean).join(' · '),
      duration: 6000,
    });
  }, [order?.driver_id, order?.status]);

  // Stripe return: poll payment status
  useEffect(() => {
    const sessionId = params.get('session_id');
    if (!sessionId) return;
    let attempts = 0;
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const s = await paymentsApi.status(sessionId);
        setPayment(s);
        if (s.payment_status === 'paid') {
          toast.success('Payment successful!');
          // Clean up the URL so refreshes don't re-poll
          params.delete('session_id');
          setParams(params, { replace: true });
          return;
        }
        if (s.status === 'expired') {
          toast.error('Payment session expired');
          return;
        }
      } catch (e) {
        // Transient poll error — swallow silently but log so we can trace
        // hanging payment confirmations in production.
        console.warn('Payment status poll transient error:', e?.message || e);
      }
      if (attempts < 8) setTimeout(poll, 2000);
      else toast.error('Could not confirm payment — check your email or contact support.');
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [params.get('session_id')]);

  // For pickup orders, auto-fetch + auto-open the QR so the customer sees it immediately.
  useEffect(() => {
    if (!order || qr) return;
    if ((order.fulfillment_type || 'delivery') !== 'pickup') return;
    let alive = true;
    orderQrApi.get(orderId)
      .then((data) => { if (alive) { setQr(data); setQrOpen(true); } })
      .catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line
  }, [order?.fulfillment_type, orderId]);

  // Flow (pickup vs delivery) derived without gating on early returns — hooks
  // must fire in the same order every render.
  const flow = useMemo(() => {
    const isPickup = (order?.fulfillment_type || 'delivery') === 'pickup';
    return (isPickup ? pickupFlow : deliveryFlow)
      .map((k) => ORDER_STATUSES.find((s) => s.key === k))
      .filter(Boolean);
  }, [order?.fulfillment_type]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header showSearch={false} />
        <div className="max-w-3xl mx-auto p-16 text-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-600 mx-auto" /></div>
      </div>
    );
  }
  if (!order) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header showSearch={false} />
        <div className="max-w-xl mx-auto p-10 text-center text-slate-500">
          Order not found. <Link to="/orders" className="text-emerald-700 underline">View all orders</Link>
        </div>
      </div>
    );
  }

  const vendor = order.vendor_lat != null ? { lat: order.vendor_lat, lng: order.vendor_lng } : null;
  // Prefer the live-polled driver coords (updated every 5s) over the snapshot
  // stored on the order document.
  const driver = driverLive?.lat != null
    ? { lat: driverLive.lat, lng: driverLive.lng, last_ping_at: driverLive.last_ping_at }
    : (order.driver_lat != null ? { lat: order.driver_lat, lng: order.driver_lng } : null);
  // Destination falls back to the vendor offset if we don't have a precise drop-off coord
  const destination = order.dest_lat != null ? { lat: order.dest_lat, lng: order.dest_lng } : null;
  const isPickup = (order.fulfillment_type || 'delivery') === 'pickup';
  const rawStepIdx = flow.findIndex((x) => x.key === order.status);
  const stepIdx = Math.max(0, rawStepIdx);
  const currentStep = rawStepIdx >= 0
    ? flow[rawStepIdx]
    : { key: order.status, label: (order.status || 'processing').replace(/_/g, ' '), icon: null, hint: 'Your order is being processed.' };

  return (
    <div className="min-h-screen bg-slate-50">
      <Header showSearch={false} />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between gap-2 flex-wrap mb-4">
          <button onClick={() => nav('/orders')} className="inline-flex items-center text-sm text-slate-600 hover:text-slate-900" data-testid="back-to-orders-btn">
            <ArrowLeft className="h-4 w-4 mr-1" /> All orders
          </button>
          <div className="flex items-center gap-2">
            <button onClick={async () => {
              try { const data = qr || await orderQrApi.get(orderId); setQr(data); setQrOpen(true); }
              catch (e) { toast.error('Could not load QR'); }
            }} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-slate-200 bg-white text-sm text-slate-700 hover:border-slate-300" data-testid="track-qr-btn">
              <QrCode className="h-4 w-4" /> {t('track.order', 'Order')} QR
            </button>
            <ShareButton title={`ShipitNow ${t('track.order', 'order')} ${orderId}`} text={t('track.share_order', 'Share order')} />
          </div>
        </div>

        {qrOpen && qr && (
          <div className={`mb-4 p-4 bg-white border rounded-2xl flex items-center gap-4 ${isPickup ? 'border-emerald-300 ring-1 ring-emerald-300 shadow-sm' : 'border-slate-200'}`} data-testid="track-qr-card">
            <img src={qr.data_url} alt="QR" className="h-32 w-32 rounded-lg border border-slate-200" />
            <div className="flex-1">
              <div className="text-xs uppercase tracking-wider text-emerald-700 font-semibold">{isPickup ? t('track.pickup_qr', 'Pickup QR') : t('track.driver_qr', 'Driver pickup QR')}</div>
              <div className="font-semibold text-slate-900 mt-0.5">{isPickup ? t('track.show_at_store', 'Show this at the store to collect') : t('track.driver_will_scan', 'Show this at the store')}</div>
              <p className="text-sm text-slate-500 mt-1">{isPickup
                ? t('track.pickup_qr_help', "The store will scan this QR to release your order. Keep it handy.")
                : t('track.driver_qr_help', "Your driver will scan this code to confirm they're picking up the right order.")}</p>
              {qr.pickup_code && (
                <div className="mt-2 inline-flex items-center gap-2 bg-slate-100 border border-slate-200 rounded-lg px-3 py-1.5" data-testid="track-qr-backup-code">
                  <span className="text-xs text-slate-500">{t('track.backup_code', "Backup code if QR won't scan:")}</span>
                  <span className="font-mono font-bold tracking-widest text-slate-900">{qr.pickup_code.slice(0, 3)} {qr.pickup_code.slice(3)}</span>
                </div>
              )}
            </div>
            <button onClick={() => setQrOpen(false)} className="p-1 text-slate-400 hover:text-slate-700"><XCircle className="h-5 w-5" /></button>
          </div>
        )}

        {payment && (
          <PaymentBanner payment={payment} />
        )}

        {order?.signed_agreement && (
          <div
            className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-2xl flex items-center justify-between gap-4"
            data-testid="declaration-download-card"
          >
            <div className="flex items-center gap-3">
              <ShieldCheck className="h-6 w-6 text-amber-600 flex-shrink-0" />
              <div>
                <div className="font-semibold text-amber-900">Signed shipment declaration</div>
                <p className="text-xs text-amber-800 mt-0.5">
                  Legal record signed by {order.signature_name}. Keep the PDF for your files.
                </p>
              </div>
            </div>
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/orders/${order.order_id}/declaration.pdf`}
              target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1.5 text-xs font-medium bg-amber-600 text-white px-3 py-2 rounded-lg hover:bg-amber-700"
              data-testid="declaration-download-btn"
            >
              <Download className="h-3.5 w-3.5" /> Download PDF
            </a>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <div className="lg:col-span-3 bg-white border border-slate-200 rounded-2xl overflow-hidden relative">
            {/* Bigger map + floating ETA badge (Uber-style) */}
            <div className="relative">
              <LiveMap vendor={vendor} driver={driver} destination={destination} />
              {driverLive?.lat != null && (
                <div className="absolute bottom-3 left-3 bg-white/95 backdrop-blur rounded-full pl-2 pr-3 h-8 shadow-sm border border-slate-200 flex items-center gap-1.5" data-testid="track-live-indicator">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-600"></span>
                  </span>
                  <span className="text-[11px] font-medium text-slate-900">
                    Live driver location{driverLive.last_ping_at ? ` · ${new Date(driverLive.last_ping_at).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit', second: '2-digit'})}` : ''}
                  </span>
                </div>
              )}
              <div className="absolute top-3 left-3 bg-white/95 backdrop-blur rounded-full pl-2 pr-3 h-9 shadow-sm border border-slate-200 flex items-center gap-2" data-testid="track-eta-badge">
                <Clock className="h-4 w-4 text-emerald-600" />
                <span className="text-sm font-semibold text-slate-900">
                  {(() => {
                    // Prefer live GPS-derived ETA when the driver is actively
                    // moving toward pickup/customer. Falls back to the static
                    // 20–30 min estimate at pre-assignment stages.
                    const liveMin = driverLive?.eta_minutes;
                    if (liveMin && ['picked_up', 'on_the_way', 'arrived_at_customer'].includes(order.status)) {
                      return `Arriving in ~${liveMin} min`;
                    }
                    if (liveMin && ['accepted', 'en_route_to_vendor', 'arrived_at_vendor'].includes(order.status)) {
                      return `Driver ~${liveMin} min from pickup`;
                    }
                    const eta = order.eta || (isPickup ? 'Ready in 20–30 min' : '20–30 min');
                    if (isPickup) return eta.match(/^ready\s+in/i) ? eta : `Ready in ${eta}`;
                    return eta.match(/^(arriv|out for)/i) ? eta : `Arrives in ${eta}`;
                  })()}
                </span>
                {driverLive?.eta_minutes && (
                  <span className="ml-1 relative flex h-2 w-2" title="Live GPS ETA — refreshes every 5 sec">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-600"></span>
                  </span>
                )}
              </div>
              <div className="absolute top-3 right-3 bg-emerald-600 text-white rounded-full px-3 h-9 shadow-sm flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider" data-testid="track-status-badge">
                {currentStep.icon ? React.createElement(currentStep.icon, { className: 'h-3.5 w-3.5' }) : null}
                {currentStep.label}
              </div>
            </div>
            <div className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs uppercase tracking-wider text-emerald-700 font-semibold">{currentStep.label}</div>
                  <h2 className="text-xl font-semibold text-slate-900 mt-1">{statusMsgs[currentStep.key] || currentStep.hint}</h2>
                </div>
                <div className="text-right text-sm text-slate-500">
                  <div>Order</div>
                  <div className="font-mono font-semibold text-slate-900" data-testid="order-id">{order.id}</div>
                </div>
              </div>

              {/* Vertical timeline — full journey visualized (mobile shows the same, stacked) */}
              <ol className="mt-5 relative border-l-2 border-slate-100 pl-5 space-y-4" data-testid="track-timeline">
                {flow.map((s, idx) => {
                  const done = idx < stepIdx;
                  const active = idx === stepIdx;
                  const Icon = s.icon || Circle;
                  return (
                    <li key={s.key} className="relative" data-testid={`timeline-step-${s.key}`}>
                      <div className={`absolute -left-[27px] top-0 h-8 w-8 rounded-full border-2 flex items-center justify-center transition-colors ${
                        done ? 'bg-emerald-600 border-emerald-600 text-white'
                             : active ? 'bg-white border-emerald-600 text-emerald-600 ring-4 ring-emerald-100'
                                      : 'bg-white border-slate-200 text-slate-300'
                      }`}>
                        {done ? <CheckCircle2 className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
                      </div>
                      <div className={`min-h-8 flex items-center ${active ? 'ml-1' : 'ml-1'}`}>
                        <div>
                          <div className={`text-sm ${done || active ? 'font-semibold text-slate-900' : 'text-slate-400'}`}>{s.label}</div>
                          {(done || active) && <div className="text-xs text-slate-500 mt-0.5">{statusMsgs[s.key] || s.hint}</div>}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ol>
            </div>
          </div>

          <div className="lg:col-span-2 space-y-5">
            {isPickup ? (
              <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="track-pickup-info">
                <h3 className="font-semibold text-slate-900">{t('track.pickup_from', 'Pickup from')}</h3>
                <div className="mt-3 text-sm text-slate-700 font-medium">
                  {(order.vendor_system || String(order.vendor_id || '').startsWith('sys-')) ? 'ShipitNow driver pickup' : (order.vendor_name || order.vendorName)}
                </div>
                {order.vendor_address && (
                  <div className="mt-1 text-sm text-slate-500 flex items-start gap-2">
                    <MapPin className="h-4 w-4 mt-0.5 text-slate-400 shrink-0" /> {order.vendor_address}
                  </div>
                )}
                <p className="mt-3 text-xs text-slate-500">
                  {t('track.pickup_eta_help', "Your order will be ready in {{eta}}. Show the QR above at the counter.", { eta: order.eta || '20–30 min' })}
                </p>
              </div>
            ) : (
              <div className="bg-white border border-slate-200 rounded-2xl p-5">
                <h3 className="font-semibold text-slate-900">Your driver</h3>
                <div className="mt-3 flex items-center gap-3">
                  <div className="h-12 w-12 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-700 text-white flex items-center justify-center font-semibold">
                    {order.driver?.name?.split(' ').map((p) => p[0]).slice(0, 2).join('') || 'M'}
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-slate-900 inline-flex items-center gap-1.5">
                      {order.driver?.name || 'Driver'}
                      {order.driver?.courier && (
                        <span className="px-1.5 py-0.5 rounded-full bg-indigo-600 text-white text-[9px] font-bold uppercase tracking-wider" data-testid="driver-courier-badge">COURIER</span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 inline-flex items-center gap-1"><Star className="h-3 w-3 fill-yellow-400 text-yellow-400" /> {order.driver?.rating} • {order.driver?.vehicle}</div>
                  </div>
                  <Button variant="outline" size="icon" className="rounded-full"><Phone className="h-4 w-4" /></Button>
                  <Button variant="outline" size="icon" className="rounded-full"><MessageCircle className="h-4 w-4" /></Button>
                </div>
              </div>
            )}

            <div className="bg-white border border-slate-200 rounded-2xl p-5">
              <h3 className="font-semibold text-slate-900">{isPickup ? t('track.pickup', 'Pickup') : t('track.delivery', 'Delivery')}</h3>
              <div className="mt-3 text-sm text-slate-600 space-y-2">
                <div className="flex items-start gap-2"><MapPin className="h-4 w-4 mt-0.5 text-slate-400" /> {isPickup ? (order.vendor_address || order.address) : order.address}</div>
                {!isPickup && order.dropoff_preference && (
                  <div className="mt-1 inline-flex items-center px-2 py-0.5 rounded-full bg-blue-50 border border-blue-100 text-blue-700 text-[11px] font-medium" data-testid="track-dropoff-pref">
                    {order.dropoff_preference === 'meet_at_door' ? 'Meet driver at door' : 'Leave at door — photo proof'}
                  </div>
                )}
                <div className="flex items-center gap-2"><Clock className="h-4 w-4 text-slate-400" /> ETA {order.eta || '20–30 min'}</div>
              </div>
            </div>

            {order.status === 'delivered' && (
              <DeliveryReceiptCard orderId={order.id} />
            )}

            <div className="bg-white border border-slate-200 rounded-2xl p-5">
              <h3 className="font-semibold text-slate-900">Items</h3>
              <ul className="mt-3 divide-y divide-slate-100">
                {order.items.map((i) => (
                  <li key={i.id} className="py-2 flex items-center justify-between text-sm">
                    <span className="text-slate-700"><b className="text-slate-900">{i.qty}×</b> {i.name}</span>
                    <span className="font-medium">{format(i.price * i.qty)}</span>
                  </li>
                ))}
              </ul>
              <div className="h-px bg-slate-200 my-3" />
              {order.pay_for_me && (
                <div className="mb-3 rounded-xl bg-indigo-50 border border-indigo-200 px-3 py-2 text-xs text-indigo-800" data-testid="track-payforme-banner">
                  <b>PAY FOR ME</b> — you paid everything to ShipitNow in one payment. We notify the store,
                  and a ShipitNow Super Driver settles the goods amount in person at pickup.
                </div>
              )}
              <div className="space-y-1.5 text-sm" data-testid="order-cost-breakdown">
                <div className="flex items-center justify-between text-slate-600">
                  <span>{t('track.goods_subtotal', 'Goods subtotal')}</span>
                  <span data-testid="order-goods-subtotal">{format(order.subtotal ?? 0)}</span>
                </div>
                <div className="flex items-center justify-between text-slate-600">
                  <span>{t('track.tax', 'Tax')}</span>
                  <span data-testid="order-tax">{format(order.tax_amount ?? 0)}</span>
                </div>
                <div className="flex items-center justify-between text-slate-600">
                  <span>{t('track.delivery_fee', 'Delivery fee')}</span>
                  <span data-testid="order-delivery-fee">{format(order.delivery_fee ?? order.deliveryFee ?? 0)}</span>
                </div>
                <DeliveryFeeBreakdown
                  breakdown={order.delivery_fee_breakdown}
                  distanceKm={order.distance_km}
                  currency={order.currency}
                  total={order.delivery_fee ?? order.deliveryFee}
                />
                <div className="flex items-center justify-between text-slate-600">
                  <span>{t('track.service_fee', 'Service fee')}</span>
                  <span data-testid="order-service-fee">{format(order.service_fee ?? order.serviceFee ?? 0)}</span>
                </div>
              </div>
              <div className="h-px bg-slate-200 my-3" />
              <div className="flex items-center justify-between text-sm font-semibold">
                <span>Total</span>
                <span data-testid="order-total">{format(order.total)}</span>
              </div>
              <p className="text-[11px] text-slate-400 mt-2">
                {t('track.total_note', 'Goods + tax go to the vendor; delivery + service fees go to ShipitNow.')}
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

const PaymentBanner = ({ payment }) => {
  if (payment.payment_status === 'paid') {
    return (
      <div className="mb-4 p-3 rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-800 flex items-center gap-2" data-testid="payment-banner-paid">
        <CheckCircle2 className="h-5 w-5" /> Payment confirmed • {(payment.amount_total / 100).toFixed(2)} {payment.currency?.toUpperCase()}
      </div>
    );
  }
  if (payment.status === 'expired' || payment.payment_status === 'unpaid') {
    return (
      <div className="mb-4 p-3 rounded-xl border border-amber-200 bg-amber-50 text-amber-800 flex items-center gap-2" data-testid="payment-banner-pending">
        <XCircle className="h-5 w-5" /> Payment not completed — please retry from cart if you still want this order.
      </div>
    );
  }
  return (
    <div className="mb-4 p-3 rounded-xl border border-slate-200 bg-white text-slate-700 flex items-center gap-2" data-testid="payment-banner-checking">
      <Loader2 className="h-4 w-4 animate-spin" /> Confirming payment…
    </div>
  );
};

// Delivery receipt CTA — only shown once an order is delivered. Lazy-mints the
// signed public receipt URL server-side so the token stays fresh.
const DeliveryReceiptCard = ({ orderId }) => {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [url, setUrl] = useState(null);
  const [copied, setCopied] = useState(false);

  const ensureUrl = async () => {
    if (url) return url;
    const { share_path } = await ordersApi.createReceiptShare(orderId);
    const full = `${window.location.origin}${share_path}`;
    setUrl(full);
    return full;
  };

  const onShare = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const shareUrl = await ensureUrl();
      if (navigator.share) {
        try {
          await navigator.share({ title: `ShipitNow receipt ${orderId}`, text: 'My delivery receipt', url: shareUrl });
        } catch (_e) { /* user canceled — no-op */ }
      } else {
        await navigator.clipboard.writeText(shareUrl);
        setCopied(true);
        toast.success('Receipt link copied');
        setTimeout(() => setCopied(false), 2500);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create share link');
    } finally { setBusy(false); }
  };

  const onOpen = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const shareUrl = await ensureUrl();
      window.open(shareUrl, '_blank', 'noopener');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not open receipt');
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-gradient-to-br from-emerald-50 to-white border border-emerald-200 rounded-2xl p-5" data-testid="delivery-receipt-card">
      <div className="flex items-start gap-3">
        <div className="h-11 w-11 rounded-xl bg-emerald-600 text-white flex items-center justify-center flex-none">
          <Receipt className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-slate-900">{t('receipt.title', 'Your delivery receipt is ready')}</h3>
          <p className="text-sm text-slate-600 mt-0.5">
            {t('receipt.subtitle', 'Share a full receipt with driver photos, timestamp and totals — no login needed to open.')}
          </p>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <Button size="sm" onClick={onOpen} disabled={busy} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="open-receipt-btn">
          <Receipt className="h-4 w-4 mr-1.5" /> Open receipt
        </Button>
        <Button size="sm" onClick={onShare} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="share-receipt-btn">
          <Share2 className="h-4 w-4 mr-1.5" /> {copied ? 'Link copied!' : 'Share receipt'}
        </Button>
      </div>
    </div>
  );
};

export default TrackOrder;

